import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../../context/BrandingContext';
import { api } from '../../services/api';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { roleLabels, roleIcons } from '../../mocks/stocks';
import { permissionsList, permissionsMatrix, categories, type RoleKey, type PermissionMatrix } from '../../mocks/permissions';
import type { RoleUtilisateur } from '../../mocks/commandes';

interface UserInfo { id: string; nom: string; role: string; }

type Tab = 'globale' | 'utilisateurs' | 'permissions' | 'parametres';

interface Utilisateur {
  id: string;
  nom: string;
  role: string;
  email: string;
  pin: string;
  avatar: string;
  actif: boolean;
}

const ALL_ROLES: string[] = ['superadmin', 'gerant', 'responsable_atelier', 'vendeur'];
const ROLES_FOR_MATRIX: RoleKey[] = ['gerant', 'responsable_atelier', 'vendeur'];

const roleColors: Record<string, string> = {
  superadmin: 'bg-amber-100 text-amber-800 border-amber-300',
  gerant: 'bg-foret-100 text-foret-800 border-foret-300',
  vendeur: 'bg-terre-100 text-terre-700 border-terre-300',
  responsable_atelier: 'bg-or-100 text-or-700 border-or-300',
};

const roleBg: Record<string, string> = {
  superadmin: 'bg-amber-500',
  gerant: 'bg-foret-500',
  vendeur: 'bg-terre-500',
  responsable_atelier: 'bg-or-500',
};

const moduleGroups = [
  {
    label: 'Dashboards',
    icon: 'ri-layout-grid-line',
    items: [
      { label: 'Vue Gérant', icon: 'ri-bar-chart-line', path: '/dashboard/gerant' },
      { label: 'Kanban Atelier', icon: 'ri-layout-masonry-line', path: '/dashboard/atelier' },
      { label: 'Dashboard Vendeur', icon: 'ri-store-2-line', path: '/dashboard/vendeur' },
      { label: 'Dashboard Coupeur', icon: 'ri-scissors-line', path: '/dashboard/coupeur' },
      { label: 'Dashboard Tailleur', icon: 'ri-sewing-thread-line', path: '/dashboard/tailleur' },
    ],
  },
  {
    label: 'Commandes & Ventes',
    icon: 'ri-shopping-bag-line',
    items: [
      { label: 'Nouvelle commande', icon: 'ri-add-circle-line', path: '/commande/nouvelle' },
      { label: 'Vente tissu', icon: 'ri-receipt-line', path: '/vente-tissu' },
    ],
  },
  {
    label: 'Gestion',
    icon: 'ri-settings-3-line',
    items: [
      { label: 'Stocks', icon: 'ri-stack-line', path: '/stocks' },
      { label: 'Nouveau produit', icon: 'ri-add-box-line', path: '/stocks/nouveau' },
      { label: 'Personnel & Paie', icon: 'ri-team-line', path: '/paie' },
      { label: 'Utilisateurs', icon: 'ri-user-settings-line', path: '/utilisateurs' },
    ],
  },
];

export default function DashboardSuperAdmin() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('globale');
  const [utilisateurs, setUtilisateurs] = useState<Utilisateur[]>([]);
  const [matrix, setMatrix] = useState<PermissionMatrix>(permissionsMatrix);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const { branding, refreshBranding } = useBranding();
  const [globalStats, setGlobalStats] = useState({
    commandes_count: 0,
    total_ca: 0,
    total_encaisse: 0,
    stock_val: 0,
    alertes_count: 0
  });

  // User management state
  const [userSearch, setUserSearch] = useState('');
  const [userFilterRole, setUserFilterRole] = useState('tous');
  const [editModal, setEditModal] = useState<Utilisateur | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ nom: '', role: 'vendeur', pin: '', email: '' });
  const [confirmDelete, setConfirmDelete] = useState<Utilisateur | null>(null);

  // Params state
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [params, setParams] = useState({
    pinLength: '4',
  });

  useEffect(() => {
    if (branding) {
      setParams(prev => ({
        ...prev,
        nomEntreprise: branding.nom,
        adresse: branding.adresse,
        telephone: branding.telephone,
        email: branding.email,
        devise: branding.devise
      }));
    }
  }, [branding]);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored && token) {
      const u = JSON.parse(stored);
      if (u.role !== 'superadmin') navigate('/login');
      else {
        setUser(u);
        fetchData(token);
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchData = async (token: string) => {
    setLoading(true);
    try {
      const [usersData, statsData] = await Promise.all([
        api.getUsers(token),
        api.getGlobalStats(token)
      ]);
      setUtilisateurs(usersData);
      setGlobalStats(statsData);

      const rolesPerms = await api.getRolePermissions(token);
      if (rolesPerms && Object.keys(rolesPerms).length > 0) {
        setMatrix(rolesPerms);
      }
    } catch (err) {
      showToast("Erreur lors de la récupération des données", 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filteredUsers = utilisateurs.filter(u => {
    const q = userSearch.toLowerCase();
    const nom = u.nom?.toLowerCase() || '';
    const email = u.email?.toLowerCase() || '';
    const matchQ = !q || nom.includes(q) || email.includes(q);
    const matchRole = userFilterRole === 'tous' || u.role === userFilterRole;
    return matchQ && matchRole;
  });

  const handleUpdateUser = async () => {
    if (!editModal) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      const updateData: any = {
        nom: editModal.nom,
        email: editModal.email,
        role: editModal.role,
        actif: editModal.actif
      };

      // N'envoyer le PIN que s'il a été modifié
      if (editModal.pin && editModal.pin !== '****' && editModal.pin.trim() !== '') {
        updateData.pin = editModal.pin;
      }

      await api.updateUser(token, editModal.id, updateData);
      showToast(`${editModal.nom} mis à jour`);
      setEditModal(null);
      fetchData(token);
    } catch (err) {
      showToast("Erreur lors de la mise à jour", 'error');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.nom.trim() || !newUser.pin.trim()) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.createUser(token, {
        nom: newUser.nom,
        role: newUser.role,
        email: newUser.email || `${newUser.nom.toLowerCase().replace(/\s+/g, '.')}@atelierbassoum.ml`,
        pin: newUser.pin,
        actif: 1
      });
      showToast(`${newUser.nom} ajouté avec succès`);
      setAddModal(false);
      setNewUser({ nom: '', role: 'vendeur', pin: '', email: '' });
      fetchData(token);
    } catch (err) {
      showToast("Erreur lors de l'ajout", 'error');
    }
  };

  const handleToggleActif = async (id: string | number) => {
    const u = utilisateurs.find(u => u.id === id);
    if (!u) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.updateUser(token, id, { actif: u.actif ? 0 : 1 });
      showToast(`${u.nom} ${u.actif ? 'désactivé' : 'activé'}`);
      fetchData(token);
    } catch (err) {
      showToast("Erreur lors de l'action", 'error');
    }
  };

  const handleDeleteUser = async (u: Utilisateur) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.deleteUser(token, u.id);
      showToast(`${u.nom} supprimé`, 'error');
      setConfirmDelete(null);
      fetchData(token);
    } catch (err) {
      showToast("Erreur lors de la suppression", 'error');
    }
  };

  const handleTogglePerm = (permId: string, role: RoleKey) => {
    setMatrix(prev => {
      // Sécurité : si la permission n'est pas encore dans la matrice, on l'initialise
      const currentPerm = prev[permId] || {
        gerant: false,
        responsable_atelier: false,
        vendeur: false
      };
      
      return {
        ...prev,
        [permId]: { ...currentPerm, [role]: !currentPerm[role] },
      };
    });
  };

  const handleSavePerms = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.updateRolePermissions(token, matrix);
      showToast('Permissions enregistrées avec succès', 'success');
    } catch (err) {
      showToast("Erreur lors de l'enregistrement des permissions", 'error');
    }
  };

  const handleSaveParams = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.updateSettings(token, {
        nom: params.nomEntreprise,
        adresse: params.adresse,
        telephone: params.telephone,
        email: params.email,
        devise: params.devise
      });
      showToast('Paramètres système enregistrés');
      refreshBranding();
    } catch (err) {
      showToast("Erreur lors de l'enregistrement", 'error');
    }
  };

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const userStats = {
    totalUsers: utilisateurs.length,
    actifs: utilisateurs.filter(u => u.actif).length,
    inactifs: utilisateurs.filter(u => !u.actif).length,
    parRole: ALL_ROLES.map(r => ({ role: r, count: utilisateurs.filter(u => u.role === r).length })),
  };

  if (!user) return null;

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'globale', label: 'Vue globale', icon: 'ri-dashboard-line' },
    { id: 'utilisateurs', label: 'Utilisateurs', icon: 'ri-team-line' },
    { id: 'permissions', label: 'Permissions', icon: 'ri-lock-2-line' },
    { id: 'parametres', label: 'Paramètres', icon: 'ri-settings-3-line' },
  ];

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="hidden md:block flex-shrink-0">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Horizontal Navigation Header */}
        <header className="bg-white border-b border-gray-100 flex-shrink-0 shadow-sm z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="font-playfair font-bold text-xl text-gray-900">
                Administration & Sécurité
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Panel global de contrôle : Utilisateurs, Permissions et Paramètres
              </p>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'utilisateurs' && (
                <button
                  onClick={() => setAddModal(true)}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap shadow-sm border border-amber-600/20"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line" /></div>
                  Ajouter un utilisateur
                </button>
              )}
              {activeTab === 'permissions' && (
                <button
                  onClick={handleSavePerms}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap shadow-sm border border-amber-600/20"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line" /></div>
                  Enregistrer
                </button>
              )}
            </div>
          </div>
          
          {/* Horizontal Tabs */}
          <div className="px-6 flex gap-8 border-t border-gray-50 overflow-x-auto hide-scrollbar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3.5 text-sm font-semibold cursor-pointer transition-all border-b-[3px] relative whitespace-nowrap flex items-center gap-2.5 ${
                  activeTab === tab.id
                    ? 'text-amber-600 border-amber-500'
                    : 'text-gray-500 border-transparent hover:text-amber-500 hover:border-amber-200'
                }`}
              >
                <i className={`${tab.icon} ${activeTab === tab.id ? 'text-amber-500' : 'opacity-60 text-lg'}`} />
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {/* ========== VUE GLOBALE ========== */}
          {activeTab === 'globale' && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Utilisateurs totaux', value: userStats.totalUsers, icon: 'ri-team-line', color: 'bg-amber-50 border-amber-200 text-amber-800' },
                  { label: 'Comptes actifs', value: userStats.actifs, icon: 'ri-user-follow-line', color: 'bg-green-50 border-green-200 text-green-800' },
                  { label: 'Comptes inactifs', value: userStats.inactifs, icon: 'ri-user-unfollow-line', color: 'bg-red-50 border-red-200 text-red-700' },
                  { label: 'Commandes (Total)', value: globalStats.commandes_count, icon: 'ri-file-list-3-line', color: 'bg-foret-50 border-foret-200 text-foret-800' },
                ].map(s => (
                  <div key={s.label} className={`rounded-2xl border px-5 py-4 ${s.color}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center"><i className={s.icon} /></div>
                      <span className="text-xs font-medium opacity-70">{s.label}</span>
                    </div>
                    <p className="text-3xl font-bold">{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Répartition par rôle */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-5 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-600"><i className="ri-pie-chart-line" /></div>
                  Répartition par rôle
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {userStats.parRole.map(({ role, count }) => (
                    <div key={role} className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${roleColors[role] || 'bg-gray-50 border-gray-200'}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${roleBg[role] || 'bg-gray-400'}`}>
                        <i className={`${roleIcons[role] || 'ri-user-line'} text-white text-lg`} />
                      </div>
                      <div>
                        <p className="text-xs font-semibold opacity-70">{roleLabels[role] || role}</p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* User list overview */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Tous les utilisateurs</h3>
                  <button onClick={() => setActiveTab('utilisateurs')} className="text-xs text-amber-600 font-semibold hover:text-amber-700 cursor-pointer whitespace-nowrap">
                    Gérer →
                  </button>
                </div>
                <div className="divide-y divide-gray-50">
                  {utilisateurs.map(u => (
                    <div key={u.id} className={`flex items-center gap-4 px-6 py-3 hover:bg-gray-50/70 ${!u.actif ? 'opacity-50' : ''}`}>
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${roleBg[u.role] || 'bg-gray-400'}`}>
                        {u.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{u.nom || 'Sans nom'}</p>
                        <p className="text-xs text-gray-400">{u.email || 'Pas d\'email'}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${roleColors[u.role] || 'bg-gray-100 text-gray-600'}`}>
                        {roleLabels[u.role] || u.role}
                      </span>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${u.actif ? 'bg-green-400' : 'bg-gray-300'}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Permissions summary */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center text-amber-600"><i className="ri-lock-2-line" /></div>
                    Résumé des permissions
                  </h3>
                  <button onClick={() => setActiveTab('permissions')} className="text-xs text-amber-600 font-semibold hover:text-amber-700 cursor-pointer whitespace-nowrap">
                    Modifier →
                  </button>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {ROLES_FOR_MATRIX.map(role => {
                    const total = permissionsList.length;
                    const granted = permissionsList.filter(p => matrix[p.id]?.[role]).length;
                    const pct = Math.round((granted / total) * 100);
                    return (
                      <div key={role} className="text-center">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2 ${roleBg[role]}`}>
                          <i className={`${roleIcons[role]} text-white text-sm`} />
                        </div>
                        <p className="text-xs font-semibold text-gray-700 truncate">{roleLabels[role]}</p>
                        <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <style>{`
                            .perm-pct-bar-${role} {
                              width: ${pct}%;
                            }
                          `}</style>
                          <div className={`h-full rounded-full ${roleBg[role]} perm-pct-bar-${role}`} />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{granted}/{total}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ========== UTILISATEURS ========== */}
          {activeTab === 'utilisateurs' && (
            <div className="space-y-5">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 max-w-sm">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-search-line" />
                  </div>
                  <input
                    id="user-search-input"
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Rechercher un utilisateur…"
                    title="Rechercher un utilisateur par nom ou email"
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  />
                </div>
                <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 overflow-x-auto">
                  <button
                    onClick={() => setUserFilterRole('tous')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${userFilterRole === 'tous' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Tous ({utilisateurs.length})
                  </button>
                  {ALL_ROLES.map(r => (
                    <button
                      key={r}
                      onClick={() => setUserFilterRole(r)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${userFilterRole === r ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      {roleLabels[r] || r} ({utilisateurs.filter(u => u.role === r).length})
                    </button>
                  ))}
                </div>
              </div>

              {/* Users Table */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Users Table (Desktop) / Cards (Mobile) */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="hidden md:block">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">Utilisateur</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Rôle</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Email</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Statut</th>
                        <th className="text-right text-xs font-semibold text-gray-500 uppercase px-6 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredUsers.length === 0 ? (
                        <tr><td colSpan={5} className="px-6 py-10 text-center text-gray-400">Aucun utilisateur trouvé</td></tr>
                      ) : filteredUsers.map(u => (
                        <tr key={u.id} className={`hover:bg-gray-50/70 transition-colors ${!u.actif ? 'opacity-50' : ''}`}>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${roleBg[u.role] || 'bg-gray-400'}`}>
                                {u.avatar || 'U'}
                              </div>
                              <p className="text-sm font-semibold text-gray-900">{u.nom}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${roleColors[u.role] || 'bg-gray-100'}`}>
                              {roleLabels[u.role] || u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">{u.email}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleToggleActif(u.id)} className={`text-xs font-semibold px-2.5 py-1 rounded-full border cursor-pointer ${u.actif ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500'}`}>
                              {u.actif ? 'Actif' : 'Inactif'}
                            </button>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <button onClick={() => setEditModal({ ...u })} title="Modifier l'utilisateur" className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100"><i className="ri-edit-line" /></button>
                              {u.role !== 'superadmin' && <button onClick={() => setConfirmDelete(u)} title="Supprimer l'utilisateur" className="w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"><i className="ri-delete-bin-line" /></button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Grid */}
                <div className="md:hidden divide-y divide-gray-100">
                  {filteredUsers.length === 0 ? (
                    <div className="p-10 text-center text-gray-400">Aucun utilisateur trouvé</div>
                  ) : filteredUsers.map(u => (
                    <div key={u.id} className={`p-4 ${!u.actif ? 'bg-gray-50 opacity-70' : ''}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold ${roleBg[u.role] || 'bg-gray-400'}`}>
                            {u.avatar || 'U'}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{u.nom}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border ${roleColors[u.role]}`}>
                          {roleLabels[u.role] || u.role}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-4 bg-gray-50 rounded-xl p-2">
                        <button 
                          onClick={() => handleToggleActif(u.id)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${u.actif ? 'bg-white text-green-600 border-green-100' : 'bg-gray-200 text-gray-500 border-gray-300'}`}
                        >
                          {u.actif ? '● ACTIF' : '○ INACTIF'}
                        </button>
                        <div className="flex gap-2">
                          <button onClick={() => setEditModal({ ...u })} title="Modifier" className="w-9 h-9 rounded-xl bg-white border border-gray-200 text-amber-600 shadow-sm flex items-center justify-center"><i className="ri-edit-line" /></button>
                          {u.role !== 'superadmin' && <button onClick={() => setConfirmDelete(u)} title="Supprimer" className="w-9 h-9 rounded-xl bg-white border border-red-100 text-red-500 shadow-sm flex items-center justify-center"><i className="ri-delete-bin-line" /></button>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ========== PERMISSIONS ========== */}
          {activeTab === 'permissions' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center text-amber-600 flex-shrink-0 mt-0.5">
                  <i className="ri-information-line" />
                </div>
                <p className="text-sm text-amber-800">
                  Le Superadmin a toujours accès à tout. Les modifications s&apos;appliquent aux autres rôles. Cliquez sur une case pour activer/désactiver la permission.
                </p>
              </div>

              {categories.map(cat => {
                const permsInCat = permissionsList.filter(p => p.categorie === cat);
                return (
                  <div key={cat} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="bg-gray-50 px-5 py-3 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-800 text-sm">{cat}</h3>
                    </div>

                    {/* Column headers */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3 min-w-[220px]">Permission</th>
                            {ROLES_FOR_MATRIX.map(role => (
                              <th key={role} className="text-center px-3 py-3 min-w-[100px]">
                                <div className="flex flex-col items-center gap-1">
                                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${roleBg[role]}`}>
                                    <i className={`${roleIcons[role]} text-white text-xs`} />
                                  </div>
                                  <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                                    {roleLabels[role].split(' ')[0]}
                                  </span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {permsInCat.map(perm => (
                            <tr key={perm.id} className="hover:bg-gray-50/60">
                              <td className="px-5 py-3">
                                <p className="text-sm font-medium text-gray-800">{perm.label}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{perm.description}</p>
                              </td>
                              {ROLES_FOR_MATRIX.map(role => {
                                const granted = matrix[perm.id]?.[role] ?? false;
                                return (
                                  <td key={role} className="px-3 py-3 text-center">
                                    <button
                                      onClick={() => handleTogglePerm(perm.id, role)}
                                      title={granted ? "Retirer la permission" : "Accorder la permission"}
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto cursor-pointer transition-all ${
                                        granted
                                          ? `${roleBg[role]} text-white`
                                          : 'bg-gray-100 text-gray-300 hover:bg-gray-200'
                                      }`}
                                    >
                                      <i className={`${granted ? 'ri-check-line' : 'ri-close-line'} text-sm font-bold`} />
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ========== PARAMETRES ========== */}
          {activeTab === 'parametres' && (
            <div className="max-w-2xl space-y-5">
              {/* Entreprise */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-600"><i className="ri-building-line" /></div>
                  Informations de l&apos;entreprise
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'nomEntreprise', label: 'Nom de l\'entreprise', placeholder: 'Atelier Bassoum', icon: 'ri-store-2-line' },
                    { key: 'telephone', label: 'Téléphone', placeholder: '+223 20 00 00 00', icon: 'ri-phone-line' },
                    { key: 'email', label: 'Email', placeholder: 'contact@atelierbassoum.ml', icon: 'ri-mail-line' },
                    { key: 'devise', label: 'Devise', placeholder: 'FCFA', icon: 'ri-money-franc-circle-line' },
                  ].map(f => (
                    <div key={f.key}>
                      <label htmlFor={`param-${f.key}`} className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                        <i className={`${f.icon} text-gray-400`} />
                        {f.label}
                      </label>
                      <input
                        id={`param-${f.key}`}
                        type="text"
                        value={(params as Record<string, string>)[f.key]}
                        onChange={e => setParams(prev => ({ ...prev, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        title={f.label}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label htmlFor="param-adresse" className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                      <i className="ri-map-pin-line text-gray-400" />
                      Adresse
                    </label>
                    <input
                      id="param-adresse"
                      type="text"
                      value={params.adresse}
                      onChange={e => setParams(prev => ({ ...prev, adresse: e.target.value }))}
                      placeholder="Adresse de l'entreprise"
                      title="Adresse du siège social"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                </div>
              </div>

              {/* Sécurité */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-600"><i className="ri-shield-check-line" /></div>
                  Sécurité & Accès
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Longueur du code PIN</label>
                    <div className="flex gap-2">
                      {['4', '5', '6'].map(n => (
                        <button
                          key={n}
                          onClick={() => setParams(prev => ({ ...prev, pinLength: n }))}
                          className={`px-5 py-2.5 rounded-xl border text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                            params.pinLength === n
                              ? 'bg-amber-500 text-white border-amber-500'
                              : 'border-gray-200 text-gray-600 hover:border-amber-300'
                          }`}
                        >
                          {n} chiffres
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Mode hors ligne', desc: 'Activer le mode offline-first', on: true },
                      { label: 'Journalisation', desc: 'Enregistrer les actions utilisateurs', on: true },
                      { label: 'Verrouillage auto', desc: 'Déconnecter après 30 min d\'inactivité', on: false },
                      { label: 'PIN unique', desc: 'Refuser les PINs en doublon', on: true },
                    ].map(opt => (
                      <div key={opt.label} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                          <p className="text-xs text-gray-400">{opt.desc}</p>
                        </div>
                        <div className={`w-10 h-5 rounded-full transition-colors cursor-pointer flex items-center ${opt.on ? 'bg-amber-500' : 'bg-gray-200'}`}>
                          <div className={`w-4 h-4 rounded-full bg-white mx-0.5 transition-transform ${opt.on ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Danger zone */}
              <div className="bg-red-50 rounded-2xl border border-red-200 p-6">
                <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-red-600"><i className="ri-error-warning-line" /></div>
                  Zone de danger
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Réinitialiser les permissions', desc: 'Remettre les permissions par défaut', icon: 'ri-refresh-line' },
                    { label: 'Exporter les données', desc: 'Télécharger toutes les données en JSON', icon: 'ri-download-2-line' },
                    { label: 'Réinitialiser l\'application', desc: 'Supprimer toutes les données (démo)', icon: 'ri-delete-bin-6-line' },
                  ].map(a => (
                    <div key={a.label} className="flex items-center justify-between bg-white rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 flex-shrink-0">
                          <i className={a.icon} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{a.label}</p>
                          <p className="text-xs text-gray-400">{a.desc}</p>
                        </div>
                      </div>
                      <button className="text-xs text-red-600 font-semibold border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 cursor-pointer whitespace-nowrap">
                        Exécuter
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSaveParams}
                className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold cursor-pointer transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line" /></div>
                Enregistrer les paramètres
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Edit User Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-5">Modifier l&apos;utilisateur</h3>
            <div className="space-y-4 mb-5">
              <div>
                <label htmlFor="edit-user-nom" className="block text-xs font-semibold text-gray-600 mb-1.5">Nom complet</label>
                <input
                  id="edit-user-nom"
                  type="text"
                  value={editModal.nom}
                  onChange={e => setEditModal(prev => prev ? { ...prev, nom: e.target.value } : null)}
                  placeholder="Nom complet"
                  title="Nom complet de l'utilisateur"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label htmlFor="edit-user-email" className="block text-xs font-semibold text-gray-600 mb-1.5">Email</label>
                <input
                  id="edit-user-email"
                  type="email"
                  name="email"
                  value={editModal.email}
                  onChange={e => setEditModal(prev => prev ? { ...prev, email: e.target.value } : null)}
                  placeholder="email@exemple.com"
                  title="Adresse email"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Rôle</label>
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {ALL_ROLES.map(r => (
                    <button
                      key={r}
                      onClick={() => setEditModal(prev => prev ? { ...prev, role: r } : null)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors text-left ${
                        editModal.role === r
                          ? 'border-amber-500 bg-amber-50 text-amber-800'
                          : 'border-gray-100 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${roleBg[r] || 'bg-gray-400'}`}>
                        <i className={`${roleIcons[r] || 'ri-user-line'} text-white text-xs`} />
                      </div>
                      <span className="text-sm font-medium">{roleLabels[r] || r}</span>
                      {editModal.role === r && <i className="ri-check-line ml-auto text-amber-600" />}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nouveau PIN (laisser vide pour ne pas changer)</label>
                <input
                  type="password"
                  placeholder="••••"
                  maxLength={6}
                  onChange={e => e.target.value && setEditModal(prev => prev ? { ...prev, pin: e.target.value } : null)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                Annuler
              </button>
              <button onClick={handleUpdateUser} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-white font-semibold text-sm cursor-pointer hover:bg-amber-600 whitespace-nowrap">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAddModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-5">Ajouter un utilisateur</h3>
            <div className="space-y-4 mb-5">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nom complet *</label>
                <input
                  type="text"
                  value={newUser.nom}
                  onChange={e => setNewUser(p => ({ ...p, nom: e.target.value }))}
                  placeholder="Ex: Mamadou Koné"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email (optionnel)</label>
                <input
                  type="email"
                  name="email"
                  value={newUser.email}
                  onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                  placeholder="mamadou.kone@atelierbassoum.ml"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Rôle *</label>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {ALL_ROLES.map(r => (
                    <button
                      key={r}
                      onClick={() => setNewUser(p => ({ ...p, role: r }))}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border cursor-pointer transition-colors text-left ${
                        newUser.role === r ? 'border-amber-500 bg-amber-50' : 'border-gray-100 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${roleBg[r] || 'bg-gray-400'} flex-shrink-0`}>
                        <i className={`${roleIcons[r] || 'ri-user-line'} text-white text-xs`} />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{roleLabels[r] || r}</span>
                      {newUser.role === r && <i className="ri-check-line ml-auto text-amber-600" />}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Code PIN *</label>
                <input
                  type="password"
                  value={newUser.pin}
                  onChange={e => setNewUser(p => ({ ...p, pin: e.target.value }))}
                  placeholder="••••"
                  maxLength={6}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setAddModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                Annuler
              </button>
              <button
                onClick={handleAddUser}
                disabled={!newUser.nom.trim() || !newUser.pin.trim()}
                className="flex-1 py-2.5 rounded-xl bg-amber-500 text-white font-semibold text-sm cursor-pointer hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-2xl" />
            </div>
            <h3 className="font-playfair font-bold text-lg text-gray-900 text-center mb-2">Supprimer l&apos;utilisateur ?</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              <strong>{confirmDelete.nom}</strong> sera définitivement supprimé. Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                Annuler
              </button>
              <button onClick={() => handleDeleteUser(confirmDelete)} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm cursor-pointer hover:bg-red-600 whitespace-nowrap">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
